import { useInfiniteQuery } from '@tanstack/react-query';

import { parseDateRange } from '@/components/data-table/filters-v1/components/data-table-date-range-picker-v2-filter';
import { FilterValues } from '@/hooks/use-filter-values';
import { flattenInfinitePages, getNextPageParam } from '@/lib/constants/pagination';
import { mapBillStatusToDTO } from '@/modules/common/mappers/bill-status.mapper';
import { mapCustomAttributeFilters } from '@/modules/common/utils/custom-attribute-filters';
import { GetBillsFilter } from '@/modules/finance/bills/types/dto';
import { formatDateToISO, normalizeToStartOfDayUTC } from '@/utils/date-formatters';

import { getBills } from '../api';

/** Base query key for the bills list. Combine with filters for the full key. */
export const BILLS_QUERY_KEY = 'bills';

export const useBills = ({ filters }: { filters: FilterValues['values'] }) => {
  return useInfiniteQuery({
    queryKey: [BILLS_QUERY_KEY, filters],
    queryFn: ({ pageParam }) => {
      const apiFilters = mapBillsFilters(filters);
      return getBills({ cursor: pageParam, filters: apiFilters });
    },
    getNextPageParam,
    initialPageParam: undefined as number | undefined,
    select: flattenInfinitePages,
  });
};

export const mapBillsFilters = (filters: FilterValues['values']): GetBillsFilter => {
  const apiFilters: GetBillsFilter = {};

  const dates = parseDateRange(
    typeof filters.billingPeriod === 'string' ? filters.billingPeriod : ''
  );

  if (dates) {
    apiFilters.startDate = formatDateToISO(normalizeToStartOfDayUTC(dates.startDate));
    apiFilters.endDate = formatDateToISO(normalizeToStartOfDayUTC(dates.endDate));
  }

  if (filters.searchString && typeof filters.searchString === 'string') {
    apiFilters.searchTerm = filters.searchString;
  }

  if (filters.status) {
    const statusArray = Array.isArray(filters.status) ? filters.status : [filters.status];
    apiFilters.statuses = statusArray.map(mapBillStatusToDTO);
  }

  if (filters.project) {
    const projectArray = Array.isArray(filters.project) ? filters.project : [filters.project];
    apiFilters.projectIds = projectArray.map(Number);
  }

  if (filters.member) {
    const memberArray = Array.isArray(filters.member) ? filters.member : [filters.member];
    apiFilters.forUserIds = memberArray.map(Number);
  }

  const customAttrFilters = mapCustomAttributeFilters(filters);
  if (customAttrFilters) {
    apiFilters.customAttributeFilters = customAttrFilters;
  }

  return apiFilters;
};
